import { useForm } from "@mantine/form";
import { GraphQL } from "@rpi/openapi-api";
import { RpiDrawerTextInput } from "@rpi/openapi-core";
import React from "react";
import { FilesInput } from "../../../components/FilesInput";
import { useAuthUser } from "../../../hooks/useAuthUser";
import {
  RpiDrawer,
  RpiDrawerForm,
  RpiDrawerOverlayProps,
  RpiDrawerProps,
  createRpiDrawerView,
} from "../../../rpi-core/drawer/RpiDrawer";
import { useUploadService } from "../../../services/useUploadService";
import { validateCustomer } from "../../../utils/validation.util";

export const UploadFilesView = createRpiDrawerView<Pick<RpiDrawerProps, "opened" | "onClose">>(
  ({ opened, onClose }) => {
    const [closing, setClosing] = React.useState(false);

    const handleClose = React.useCallback(
      (delay?: boolean) => {
        if (closing) return;

        setClosing(true);
        if (delay) {
          return setTimeout(onClose, 1000);
        }
        return onClose();
      },
      [onClose, closing]
    );

    return (
      <RpiDrawer title="Upload Files" opened={opened} onClose={handleClose}>
        <CreateUpload onClose={() => handleClose(true)} />
      </RpiDrawer>
    );
  }
);

interface CreateUploadProps {
  onClose: () => void;
}

const CreateUpload: React.FC<CreateUploadProps> = ({ onClose }) => {
  const user = useAuthUser();

  const { upload, status, reset } = useUploadService({
    withCollectionPoll: true,
    onComplete: onClose,
    onError: onClose,
  });

  const form = useForm<
    Omit<NonNullable<GraphQL.CreateUploadMutationVariables["input"]>, "files"> & {
      files: Array<{
        file: File;
        description: string | undefined;
      }>;
    }
  >({
    initialValues: {
      customerPublicId: user.id!,
      files: [],
    },

    validate: {
      customerPublicId: validateCustomer,
    },
  });

  React.useEffect(() => reset, []);

  const handleSubmit = React.useCallback(
    async ({ customerPublicId, files }: typeof form.values) => {
      const filesInput: GraphQL.UploadingFilesInput[] = [];
      const uploadFiles: File[] = [];

      for (const { file, description } of files) {
        filesInput.push({
          fileName: file.name,
          fileSize: file.size,
          contentType: file.type,
          taxDocument: false,
          ...(description && { description }),
        });
        uploadFiles.push(file);
      }

      return upload(
        {
          customerPublicId,
          files: filesInput,
        },
        uploadFiles
      );
    },
    [upload]
  );

  const overlay: RpiDrawerOverlayProps | undefined = React.useMemo(() => {
    if (!status) return;

    switch (status.status) {
      case "loading":
        return {
          type: "loading",
          message: status.message,
          description: status.description || "Please wait, do not close this screen.",
        };

      case "error":
        return {
          type: "error",
          message: status.message,
          description: status.description || "Please try again later.",
        };

      case "success":
        return {
          type: "success",
          message: status.message,
          description: status.description,
        };
    }
  }, [status]);

  return (
    <RpiDrawerForm
      button="Upload"
      overlay={overlay}
      disabled={!form.values.files.length}
      onSubmit={form.onSubmit(handleSubmit)}
    >
      <FilesInput
        defaultOpen
        defaultProps={{
          description: undefined as string | undefined,
        }}
        inputs={(setProps) => (
          <RpiDrawerTextInput
            label="Description"
            onChange={(e) => setProps({ description: e.target.value || undefined })}
          />
        )}
        onChange={(files) => form.setValues({ files })}
      />
    </RpiDrawerForm>
  );
};
